/**
 * Copyright © 2025 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  CircularProgress,
  Alert
} from '@mui/material';
import { authenticateWithPasskey } from '../../utils/webauthn';
import mfaService from '../../api/mfaService';
import {
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  StyledCancelButton,
  StyledConfirmButton,
  DialogContentContainer,
  IconWrapper,
  MessageText,
  TitleText,
  AlertWrapper,
  StyledFingerprintIcon
} from './styles';

const MfaVerificationModal = ({ 
  open, 
  tempSessionId, 
  passkeyOptions,
  onSuccess, 
  onCancel,
  username
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (open && passkeyOptions && !verifying) {
      handleVerifyPasskey();
    }
  }, [open, passkeyOptions]);

  const handleVerifyPasskey = async () => {
    if (verifying) return;
    
    setVerifying(true);
    setLoading(true);
    setError(null);

    try {
      const credential = await authenticateWithPasskey(passkeyOptions);
      const response = await mfaService.verifyMFA(tempSessionId, credential);

      onSuccess(response);
    } catch (err) {
      setError(err.message || 'Passkey verification failed. Please try again.');
      setVerifying(false);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setError(null);
    setVerifying(false);
    onCancel();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleCancel}
      maxWidth="xs"
      fullWidth
    >
      <StyledDialogTitle>
        <TitleText>
          Sign In
        </TitleText>
      </StyledDialogTitle>

      <StyledDialogContent>
        <DialogContentContainer>
          {loading ? (
            <>
              <IconWrapper>
                <StyledFingerprintIcon />
              </IconWrapper>
              <MessageText>
                {verifying 
                  ? 'Authenticating with your passkey...'
                  : 'Preparing verification...'
                }
              </MessageText>
              <CircularProgress />
            </>
          ) : (
            <>
              <IconWrapper>
                <StyledFingerprintIcon />
              </IconWrapper>
              <MessageText>
                {`Sign in to "admin.ownerstable.com" with your passkey${username ? ` for "${username}"` : ''}`}
              </MessageText>
              
              {error && (
                <AlertWrapper>
                  <Alert severity="error">
                    {error}
                  </Alert>
                </AlertWrapper>
              )}
            </>
          )}
        </DialogContentContainer>
      </StyledDialogContent>

      <StyledDialogActions>
        <StyledCancelButton
          onClick={handleCancel}
          disabled={loading}
        >
          Cancel
        </StyledCancelButton>
        
        {error && (
          <StyledConfirmButton
            onClick={handleVerifyPasskey}
            variant="contained"
            disabled={loading}
          >
            Try Again
          </StyledConfirmButton>
        )}
      </StyledDialogActions>
    </Dialog>
  );
};

export default MfaVerificationModal;
